import { Injectable } from '@angular/core';
import { ApplicationConfig, SystemDetails, SystemTypeConstants } from '@frontend-site/shared/data';
import {
    PermissionsService,
    isSandboxSystem,
    isSapOrOracleSystem,
    SessionRepository,
    SystemStore,
    SystemService
} from '@frontend-site/shared/core';
import { combineLatest, filter, map, Observable } from 'rxjs';
import { MainHeaderMenu, MenuStates, SettingMainHeaderMenu } from '../../models';
import { MenuBuilderService } from './menu-builder';
import { MENU_IDS } from '../menu-ids';

@Injectable()
export class SettingsMenuBuilderService implements MenuBuilderService {
    get menuState(): MenuStates {
        return MenuStates.SETTINGS;
    }

    constructor(
        private readonly systemStore: SystemStore,
        private readonly sessionStore: SessionRepository,
        private readonly permissionsService: PermissionsService,
        private readonly systemService: SystemService
    ) {}

    build$(): Observable<MainHeaderMenu | null> {
        return combineLatest([this.systemStore.systemDetails$, this.sessionStore.applicationConfig$]).pipe(
            filter(([systemDetails]) => !!systemDetails),
            map(([systemDetails, appConfig]) => this.createMenu(systemDetails as SystemDetails, appConfig as ApplicationConfig))
        );
    }

    private createMenu(systemDetails: SystemDetails, appConfig: ApplicationConfig): MainHeaderMenu | null {
        const menu = new SettingMainHeaderMenu(this.menuState);

        if (this.showConfigurationMenu(systemDetails?.systemType)) {
            menu.addMenuItem({
                id: MENU_IDS.SETTINGS.CONFIGURATION,
                label: 'SystemSetup.NavBar.configuration',
                routePath: `/systemSetup/${systemDetails.systemId}/main`
            });
        }

        if (this.showCodeBox(systemDetails, appConfig.isExecuteUser, appConfig.iaEnabled)) {
            menu.addMenuItem({
                id: MENU_IDS.SETTINGS.CODE_BOX,
                label: 'Code Box',
                routePath: `/systemSetup/${systemDetails.systemId}/uploadEtl`
            });
        }

        if (this.showUsers(systemDetails)) {
            menu.addMenuItem({
                id: MENU_IDS.SETTINGS.USERS,
                label: 'SystemSetup.NavBar.users',
                routePath: `/systemSetup/${systemDetails.systemId}/userManag`
            });
        }

        if (this.showUserGroups(systemDetails)) {
            menu.addMenuItem({
                id: MENU_IDS.SETTINGS.USER_GROUPS,
                label: 'SystemSetup.NavBar.userGroups',
                routePath: `/settings/${systemDetails.systemId}/userGroups/list`
            });
        }

        if (this.permissionsService.hasPermissions('Roles and Permissions : manage', systemDetails?.projectIds)) {
            menu.addMenuItem({
                id: MENU_IDS.SETTINGS.ROLES,
                label: 'SystemSetup.NavBar.rolesManagement',
                routePath: `/systemSetup/${systemDetails.systemId}/rolesManage`
            });
        }

        return menu;
    }

    private showUsers(systemDetails: SystemDetails): boolean {
        return systemDetails.canManageUsers;
    }

    private showUserGroups(systemDetails: SystemDetails): boolean {
        return systemDetails.canManageUserGroups;
    }

    private showConfigurationMenu(systemType: string): boolean {
        return (
            systemType === SystemTypeConstants.SAP ||
            systemType === SystemTypeConstants.ORACLE ||
            systemType === SystemTypeConstants.SALESFORCE
        );
    }

    private showCodeBox(systemDetails: SystemDetails, isExecuteUser: boolean, iaEnabled: boolean): boolean {
        return (
            isSapOrOracleSystem(systemDetails.systemType) &&
            (isExecuteUser ||
                (!isSandboxSystem(systemDetails.systemName) && (!this.systemService.isUatOnly(systemDetails.systemId) || iaEnabled)))
        );
    }
}
